import React from 'react';
import { ColorName, colors } from '@utils/colors';

export type IconProps = {
  className?: string;
  fill?: ColorName;
  stroke?: ColorName;
  width?: number;
  height?: number;
  ariaHidden?: boolean;
};

const ArrowLeft = ({
  className,
  fill = 'black90',
  width = 8,
  height = 12,
  ariaHidden
}: IconProps) => (
  <svg width={width} height={height} fill="none" className={className} aria-hidden={ariaHidden}>
    <path fill={colors[fill]} d="M7.41 10.59 2.83 6l4.58-4.59L6 0 0 6l6 6 1.41-1.41Z" />
  </svg>
);

const ArrowRight = ({
  className,
  fill = 'black90',
  width = 16,
  height = 16,
  ariaHidden
}: IconProps) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 16 16"
    className={className}
    aria-hidden={ariaHidden}
  >
    <path fill={colors[fill]} d="M5.727 11.053 8.78 8 5.727 4.94l.94-.94 4 4-4 4z" />
  </svg>
);

const Checkmark = ({
  className,
  fill = 'lightGreen',
  width = 13,
  height = 10,
  ariaHidden
}: IconProps) => (
  <svg className={className} width={width} height={height} aria-hidden={ariaHidden}>
    <path
      d="M2.207 4.815.793 6.23l3.681 3.683 7.732-7.704L10.794.79 4.477 7.086l-2.27-2.27Z"
      fill={colors[fill]}
    />
  </svg>
);

const Check = ({ className, fill = 'black60', width = 24, height = 24, ariaHidden }: IconProps) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    aria-hidden={ariaHidden}
    className={className}
  >
    <path d="M21 7 9 19l-5.5-5.5 1.41-1.41L9 16.17 19.59 5.59z" fill={colors[fill]} />
  </svg>
);

const Close = ({ className, fill = 'black60', width = 14, height = 14, ariaHidden }: IconProps) => (
  <svg
    className={className}
    width={width}
    height={height}
    aria-hidden={ariaHidden}
    viewBox="0 0 14 14"
  >
    <path
      d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z"
      fill={colors[fill]}
    />
  </svg>
);

const DoubleHouse = ({
  className,
  fill = 'black90',
  width = 48,
  height = 28,
  ariaHidden
}: IconProps) => (
  <svg className={className} width={width} height={height} aria-hidden={ariaHidden}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.5 19.932v7.941H.176v-13.5l11.331-12.04 1.375-1.46 1.35 1.483 10.132 11.125 9.448-11.077L35.117.873l1.38 1.465 11.326 12.035v13.5H37.5v-7.941H32.735v7.941h-17.47v-7.941H10.5Zm12.676 5.941h-5.911v-7.941H8.5v7.941H2.176V15.166l10.682-11.35 10.318 11.331v10.726Zm2-10.263v10.263h5.56v-7.941H39.5v7.941h6.323V15.166L35.191 3.87 25.176 15.61Z"
      fill={colors[fill]}
    />
  </svg>
);

const Exclamationmark = ({
  className,
  fill = 'black90',
  width = 24,
  height = 24,
  ariaHidden
}: IconProps) => (
  <svg
    className={className}
    width={width}
    height={height}
    aria-hidden={ariaHidden}
    viewBox="0 0 48 48"
  >
    <path
      d="M20 14h8l-2 12h-4l-2-12zm4 20a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"
      fill={colors[fill]}
      fillRule="nonzero"
    />
  </svg>
);

const House = ({ className, fill = 'black90', width = 26, height = 28, ariaHidden }: IconProps) => (
  <svg
    className={className}
    width={width}
    height={height}
    viewBox="0 0 26 28"
    fill="none"
    aria-hidden={ariaHidden}
  >
    <path
      d="M24.7057 14.7696V26.873H16.3822V19.9319V18.9319H15.3822H10.6175H9.61747V19.9319V26.873H1.29395V14.7696L12.9998 2.33212L24.7057 14.7696Z"
      stroke={colors[fill]}
      strokeWidth="2"
    />
  </svg>
);

const Info = ({
  className,
  fill = 'black90',
  width = 20,
  height = 21,
  ariaHidden,
  isOutlined = false
}: IconProps & { isOutlined?: boolean }) =>
  isOutlined ? (
    <svg
      className={className}
      width={width}
      height={height}
      aria-hidden={ariaHidden}
      viewBox="0 0 24 24"
    >
      <path
        fill={colors[fill]}
        d="M11 9h2V7h-2v2zm1 11c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-18C6.477 2 2 6.477 2 12A10 10 0 1 0 12 2zm-1 15h2v-6h-2v6z"
      />
    </svg>
  ) : (
    <svg
      className={className}
      width={width}
      height={height}
      aria-hidden={ariaHidden}
      viewBox="0 0 20 21"
    >
      <path
        fill={colors[fill]}
        d="M10 0.972656C4.48 0.972656 0 5.45266 0 10.9727C0 16.4927 4.48 20.9727 10 20.9727C15.52 20.9727 20 16.4927 20 10.9727C20 5.45266 15.52 0.972656 10 0.972656ZM11 15.9727H9V9.97266H11V15.9727ZM11 7.97266H9V5.97266H11V7.97266Z"
      />
    </svg>
  );

const TrippleHouse = ({
  className,
  fill = 'black90',
  width = 74,
  height = 28,
  ariaHidden
}: IconProps) => (
  <svg className={className} width={width} height={height} aria-hidden={ariaHidden}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.617 21.932v-2H15.383v7.941h19.235v-7.941H39.383v7.941H58.618v-7.941h4.764v7.941h10.324v-13.5L62.373 2.333 61 .872l-1.373 1.46L49 13.622 38.373 2.333 37 .872l-1.373 1.46L25 13.622 14.373 2.333 13 .872l-1.373 1.46L.294 14.372v13.5h10.323v-5.941Zm-2 3.941v-7.941h8.765v7.941h6.324V15.166L13 3.791 2.294 15.166v10.707h6.323Zm24 0v-7.941h8.765v7.941h6.324V15.166L37 3.791 26.294 15.166v10.707h6.323Zm24-5.941v5.941h-6.323V15.166L61 3.791l10.706 11.375v10.707h-6.324v-7.941h-8.764v2Z"
      fill={colors[fill]}
    />
  </svg>
);

const RoofFlat = ({
  className,
  fill = 'white90',
  stroke = 'black90',
  width = 50,
  height = 12,
  ariaHidden
}: IconProps) => (
  <svg className={className} width={width} height={height} fill="none" aria-hidden={ariaHidden}>
    <path
      fill={colors[fill]}
      stroke={colors[stroke]}
      strokeWidth="2"
      d="M4.113 6h21v5h-21zM25.113 6h21v5h-21zM1.113 1h47v5h-47z"
    />
  </svg>
);

const Roof15Deg = ({
  className,
  fill = 'white90',
  stroke = 'black90',
  width = 52,
  height = 26,
  ariaHidden
}: IconProps) => (
  <svg className={className} width={width} height={height} fill="none" aria-hidden={ariaHidden}>
    <path fill={colors[fill]} stroke={colors[stroke]} strokeWidth="2" d="M5.371 19.555h21v5h-21z" />
    <path
      fill={colors[fill]}
      stroke={colors[stroke]}
      strokeWidth="2"
      d="M26.371 19.555h21v5h-21z"
    />
    <path
      d="M5.727 24.105v-5.194l21-4.565v9.76h-21ZM26.727 24.105V12.846l21-6.39v17.65h-21Z"
      fill={colors[fill]}
      stroke={colors[stroke]}
      strokeWidth="2"
    />
    <path
      fill={colors[fill]}
      stroke={colors[stroke]}
      strokeWidth="2"
      d="M1.653 19.059 48.97 1.837l1.71 4.698L3.364 23.757z"
    />
  </svg>
);

const Roof30Deg = ({
  className,
  fill = 'white90',
  stroke = 'black90',
  width = 53,
  height = 36,
  ariaHidden
}: IconProps) => (
  <svg className={className} width={width} height={height} fill="none" aria-hidden={ariaHidden}>
    <path
      d="M6.71 34.992v-5.405l21-11.413v16.818h-21ZM27.71 34.992v-17.37l21-10.043v27.413h-21Z"
      fill={colors[fill]}
      stroke={colors[stroke]}
      strokeWidth="2"
    />
    <path
      fill={colors[fill]}
      stroke={colors[stroke]}
      strokeWidth="2"
      d="M1.996 28.944 48.805 1.919l2.5 4.33-46.81 27.025z"
    />
  </svg>
);

const Roof45Deg = ({
  className,
  fill = 'white90',
  stroke = 'black90',
  width = 49,
  height = 45,
  ariaHidden
}: IconProps) => (
  <svg className={className} width={width} height={height} fill="none" aria-hidden={ariaHidden}>
    <path
      d="m4.71 43.492-.693-.499 18.95-15.86v16.36H4.71ZM23.388 43.555V26.982l21-20.087v36.66h-21Z"
      fill={colors[fill]}
      stroke={colors[stroke]}
      strokeWidth="2"
    />
    <path
      fill={colors[fill]}
      stroke={colors[stroke]}
      strokeWidth="2"
      d="m2.027 42.445 41.03-41.029 3.535 3.536L5.562 45.98z"
    />
  </svg>
);

const Savings = ({
  className,
  fill = 'black90',
  width = 24,
  height = 24,
  ariaHidden
}: IconProps) => (
  <svg
    className={className}
    width={width}
    height={height}
    viewBox="0 0 24 24"
    aria-hidden={ariaHidden}
  >
    <g fill="none" fillRule="evenodd">
      <path d="M0 .429h24v23.143H0z" />
      <path
        fill={colors[fill]}
        d="m19.83 7.66-2.27-2.188c.07-.405.18-.781.32-1.11.08-.173.12-.356.12-.558 0-.8-.67-1.447-1.5-1.447-1.64 0-3.09.762-4 1.929h-5C4.46 4.286 2 6.658 2 9.589c0 2.932 2.5 11.09 2.5 11.09H10V18.75h2v1.929h5.5l1.68-5.39 2.82-.907V7.66h-2.17zM13 9.108H8V7.18h5v1.928zm3 1.929c-.55 0-1-.434-1-.965 0-.53.45-.964 1-.964s1 .434 1 .964-.45.965-1 .965z"
      />
    </g>
  </svg>
);

const Verify = ({
  className,
  fill = 'black90',
  width = 24,
  height = 24,
  ariaHidden
}: IconProps) => (
  <svg
    className={className}
    width={width}
    height={height}
    viewBox="0 0 24 24"
    aria-hidden={ariaHidden}
  >
    <path
      d="m10 17-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8zm2-16L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"
      fill={colors[fill]}
      fillRule="nonzero"
    />
  </svg>
);

const RoofNorth = ({ width = 42, height = 42, className, ariaHidden }: IconProps) => (
  <svg
    className={className}
    id="Ebene_1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 42.97 31.81"
    width={width}
    height={height}
    aria-hidden={ariaHidden}
  >
    <defs>
      <style>
        {`
      .cls-1 {
        fill: #b2940e;
      }
      .cls-1,
      .cls-2 {
        stroke: #000;
        stroke-miterlimit: 10;
      }
      .cls-3,
      .cls-4 {
        fill: #000;
        stroke-width: 0px;
      }
      .cls-2 {
        fill: #ffcd02;
      }
      .cls-4 {
        opacity: 0.25;
      }`}
      </style>
    </defs>
    <polygon className="cls-4" points="5.66 0 38.97 0 42.97 13.61 38.97 26.49 5.66 26.49 5.66 0" />
    <rect
      className="cls-1"
      x=".52"
      y="4.71"
      width="33.31"
      height="13.32"
      transform="translate(34.34 22.74) rotate(180)"
    />
    <rect
      className="cls-2"
      x=".5"
      y="17.98"
      width="33.31"
      height="13.32"
      transform="translate(34.31 49.29) rotate(180)"
    />
    <rect
      className="cls-3"
      x="25.81"
      y="11.73"
      width="4.75"
      height="4.75"
      transform="translate(56.37 28.22) rotate(180)"
    />
    <rect
      className="cls-3"
      x="25.84"
      y="6.3"
      width="4.72"
      height="4.72"
      transform="translate(56.4 17.32) rotate(180)"
    />
    <rect
      className="cls-3"
      x="20.29"
      y="11.73"
      width="4.75"
      height="4.75"
      transform="translate(45.33 28.22) rotate(180)"
    />
    <rect
      className="cls-3"
      x="20.32"
      y="6.3"
      width="4.72"
      height="4.72"
      transform="translate(45.36 17.32) rotate(180)"
    />
    <rect
      className="cls-3"
      x="14.77"
      y="11.73"
      width="4.75"
      height="4.75"
      transform="translate(34.29 28.22) rotate(180)"
    />
    <rect
      className="cls-3"
      x="14.8"
      y="6.3"
      width="4.72"
      height="4.72"
      transform="translate(34.32 17.32) rotate(180)"
    />
    <rect
      className="cls-3"
      x="9.25"
      y="11.73"
      width="4.75"
      height="4.75"
      transform="translate(23.25 28.22) rotate(180)"
    />
    <rect
      className="cls-3"
      x="9.28"
      y="6.3"
      width="4.72"
      height="4.72"
      transform="translate(23.28 17.32) rotate(180)"
    />
    <rect
      className="cls-3"
      x="3.78"
      y="11.73"
      width="4.75"
      height="4.75"
      transform="translate(12.31 28.22) rotate(180)"
    />
    <rect
      className="cls-3"
      x="3.81"
      y="6.3"
      width="4.72"
      height="4.72"
      transform="translate(12.34 17.32) rotate(180)"
    />
  </svg>
);

const RoofWest = ({ width = 42, height = 42, className, ariaHidden }: IconProps) => (
  <svg
    className={className}
    id="Ebene_1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 31.41 42.58"
    width={width}
    height={height}
    aria-hidden={ariaHidden}
  >
    <defs>
      <style>
        {`
        .cls-1 {
          fill: #b2940e;
        }
        .cls-1,
        .cls-2 {
          stroke: #000;
          stroke-miterlimit: 10;
        }
        .cls-2 {
          fill: #ffcd02;
        }
        .cls-3 {
          fill: #262626;
        }
        .cls-3,
        .cls-4 {
          stroke-width: 0px;
        }
        .cls-4 {
          fill: #000;
          opacity: 0.25;
        }
        `}
      </style>
    </defs>
    <polygon className="cls-4" points="31.41 4 31.41 37.31 4.92 37.31 4.92 4 18.16 0 31.41 4" />
    <rect
      className="cls-2"
      x="-9.49"
      y="18.74"
      width="33.31"
      height="13.32"
      transform="translate(32.57 18.24) rotate(90)"
    />
    <rect
      className="cls-1"
      x="3.78"
      y="18.76"
      width="33.31"
      height="13.32"
      transform="translate(45.86 4.99) rotate(90)"
    />
    <rect
      className="cls-3"
      x="7.52"
      y="12.02"
      width="4.75"
      height="4.75"
      transform="translate(24.29 4.49) rotate(90)"
    />
    <rect
      className="cls-3"
      x="2.09"
      y="12.02"
      width="4.72"
      height="4.72"
      transform="translate(18.83 9.92) rotate(90)"
    />
    <rect
      className="cls-3"
      x="7.52"
      y="17.54"
      width="4.75"
      height="4.75"
      transform="translate(29.81 10.01) rotate(90)"
    />
    <rect
      className="cls-3"
      x="2.09"
      y="17.54"
      width="4.72"
      height="4.72"
      transform="translate(24.35 15.44) rotate(90)"
    />
    <rect
      className="cls-3"
      x="7.52"
      y="23.06"
      width="4.75"
      height="4.75"
      transform="translate(35.33 15.53) rotate(90)"
    />
    <rect
      className="cls-3"
      x="2.09"
      y="23.06"
      width="4.72"
      height="4.72"
      transform="translate(29.87 20.96) rotate(90)"
    />
    <rect
      className="cls-3"
      x="7.52"
      y="28.58"
      width="4.75"
      height="4.75"
      transform="translate(40.85 21.05) rotate(90)"
    />
    <rect
      className="cls-3"
      x="2.09"
      y="28.58"
      width="4.72"
      height="4.72"
      transform="translate(35.39 26.48) rotate(90)"
    />
    <rect
      className="cls-3"
      x="7.52"
      y="34.04"
      width="4.75"
      height="4.75"
      transform="translate(46.32 26.52) rotate(90)"
    />
    <rect
      className="cls-3"
      x="2.09"
      y="34.04"
      width="4.72"
      height="4.72"
      transform="translate(40.86 31.95) rotate(90)"
    />{' '}
  </svg>
);

const RoofSouth = ({ width = 42, height = 42, className, ariaHidden }: IconProps) => (
  <svg
    className={className}
    id="Ebene_1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 42.53 31.81"
    width={width}
    height={height}
    aria-hidden={ariaHidden}
  >
    <defs>
      <style>
        {`
      .cls-1 {
        fill: #b2940e;
      }
      .cls-1,
      .cls-2 {
        stroke: #000;
        stroke-miterlimit: 10;
      }
      .cls-2 {
        fill: #ffcd02;
      }
      .cls-3 {
        fill: #262626;
      }
      .cls-3,
      .cls-4 {
        stroke-width: 0px;
      }
      .cls-4 {
        fill: #000;
        opacity: 0.25;
      }
      `}
      </style>
    </defs>
    <polygon className="cls-4" points="5.23 0 38.53 0 42.53 13.25 38.53 26.49 5.23 26.49 5.23 0" />
    <rect className="cls-2" x=".5" y="17.98" width="33.31" height="13.32" />
    <rect className="cls-1" x=".52" y="4.71" width="33.31" height="13.32" />
    <rect className="cls-3" x="3.76" y="19.53" width="4.75" height="4.75" />
    <rect className="cls-3" x="3.76" y="24.99" width="4.72" height="4.72" />
    <rect className="cls-3" x="9.28" y="19.53" width="4.75" height="4.75" />
    <rect className="cls-3" x="9.28" y="24.99" width="4.72" height="4.72" />
    <rect className="cls-3" x="14.8" y="19.53" width="4.75" height="4.75" />
    <rect className="cls-3" x="14.8" y="24.99" width="4.72" height="4.72" />
    <rect className="cls-3" x="20.32" y="19.53" width="4.75" height="4.75" />
    <rect className="cls-3" x="20.32" y="24.99" width="4.72" height="4.72" />
    <rect className="cls-3" x="25.79" y="19.53" width="4.75" height="4.75" />
    <rect className="cls-3" x="25.79" y="24.99" width="4.72" height="4.72" />
  </svg>
);

const RoofEast = ({ width = 42, height = 42, className, ariaHidden }: IconProps) => (
  <svg
    className={className}
    id="Ebene_1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 31.69 41.58"
    width={width}
    height={height}
    aria-hidden={ariaHidden}
  >
    <defs>
      <style>
        {`
        .cls-1 {
          fill: #b2940e;
        }
        .cls-1,
        .cls-2 {
          stroke: #000;
          stroke-miterlimit: 10;
        }
        .cls-3,
        .cls-4 {
          fill: #000;
          stroke-width: 0px;
        }
        .cls-2 {
          fill: #ffcd02;
        }
        .cls-4 {
          opacity: 0.25;
        }
      `}
      </style>
    </defs>
    <polygon className="cls-4" points="31.69 3 31.69 36.31 5.2 36.31 5.2 3 17.7 0 31.69 3" />
    <rect
      className="cls-1"
      x="3.78"
      y="17.76"
      width="33.31"
      height="13.32"
      transform="translate(-3.99 44.86) rotate(-90)"
    />
    <rect
      className="cls-2"
      x="-9.49"
      y="17.74"
      width="33.31"
      height="13.32"
      transform="translate(-17.24 31.57) rotate(-90)"
    />
    <rect
      className="cls-3"
      x="15.32"
      y="33.06"
      width="4.75"
      height="4.75"
      transform="translate(-17.74 53.14) rotate(-90)"
    />
    <rect
      className="cls-3"
      x="20.78"
      y="33.09"
      width="4.72"
      height="4.72"
      transform="translate(-12.31 58.6) rotate(-90)"
    />
    <rect
      className="cls-3"
      x="15.32"
      y="27.54"
      width="4.75"
      height="4.75"
      transform="translate(-12.22 47.62) rotate(-90)"
    />
    <rect
      className="cls-3"
      x="20.78"
      y="27.57"
      width="4.72"
      height="4.72"
      transform="translate(-6.79 53.08) rotate(-90)"
    />
    <rect
      className="cls-3"
      x="15.32"
      y="22.02"
      width="4.75"
      height="4.75"
      transform="translate(-6.7 42.1) rotate(-90)"
    />
    <rect
      className="cls-3"
      x="20.78"
      y="22.05"
      width="4.72"
      height="4.72"
      transform="translate(-1.27 47.56) rotate(-90)"
    />
    <rect
      className="cls-3"
      x="15.32"
      y="16.5"
      width="4.75"
      height="4.75"
      transform="translate(-1.18 36.58) rotate(-90)"
    />
    <rect
      className="cls-3"
      x="20.78"
      y="16.53"
      width="4.72"
      height="4.72"
      transform="translate(4.25 42.04) rotate(-90)"
    />
    <rect
      className="cls-3"
      x="15.32"
      y="11.03"
      width="4.75"
      height="4.75"
      transform="translate(4.29 31.11) rotate(-90)"
    />
    <rect
      className="cls-3"
      x="20.78"
      y="11.06"
      width="4.72"
      height="4.72"
      transform="translate(9.72 36.57) rotate(-90)"
    />
  </svg>
);

const FlatRoof = ({ width = 42, height = 42, className, ariaHidden }: IconProps) => (
  <svg
    width={width}
    height={height}
    className={className}
    id="Ebene_1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 26.2 17.28"
    aria-hidden={ariaHidden}
  >
    <defs>
      <style>
        {`
        .cls-1 {
          fill: #fff;
          stroke: #1d1d1b;
        }
        .cls-1,
        .cls-2,
        .cls-3 {
          stroke-miterlimit: 10;
        }
        .cls-2 {
          fill: #ffcd02;
          stroke: #000;
        }
        .cls-3 {
          fill: rgba(0, 0, 0, 0.25);
          stroke: #020202;
        }
      `}
      </style>
    </defs>
    <rect className="cls-1" x=".98" y="2.25" width="24.23" height="14.54" />
    <rect className="cls-2" x=".5" y=".5" width="25.2" height="2.61" />
    <rect className="cls-3" x="15.3" y="7.09" width="4.85" height="9.69" />
    <rect
      className="cls-3"
      x="5.37"
      y="6.85"
      width="4.85"
      height="5.33"
      transform="translate(-1.72 17.31) rotate(-90)"
    />
  </svg>
);

const SlopedRoof = ({ width = 42, height = 42, className, ariaHidden }: IconProps) => (
  <svg
    width={width}
    height={height}
    className={className}
    id="Ebene_1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 30.12 19.28"
    aria-hidden={ariaHidden}
  >
    <defs>
      <style>
        {`
        .cls-1 {
          fill: #fff;
        stroke: #1d1d1b;
      }
        .cls-1,
        .cls-2,
        .cls-3 {
          stroke - miterlimit: 10;
      }
        .cls-2 {
          fill: #ffcd02;
        stroke: #000;
      }
        .cls-3 {
          fill: rgba(0, 0, 0, 0.25);
        stroke: #020202;
      }
      `}
      </style>
    </defs>
    <rect className="cls-1" x="3.65" y="5.1" width="22.81" height="13.69" />
    <polygon className="cls-2" points="15.06 .53 2.78 5.1 27.34 5.1 15.06 .53" />
    <rect className="cls-3" x="17.13" y="9.66" width="4.56" height="9.13" />
    <rect
      className="cls-3"
      x="7.78"
      y="9.43"
      width="4.56"
      height="5.02"
      transform="translate(-1.88 22) rotate(-90)"
    />
  </svg>
);

const NormalRoof = ({ width = 42, height = 42, className, ariaHidden }: IconProps) => (
  <svg
    width={width}
    height={height}
    className={className}
    id="Ebene_1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 27.52 23.88"
    aria-hidden={ariaHidden}
  >
    <defs>
      <style>
        {`
        .cls-1 {
          fill: #fff;
          stroke: #1d1d1b;
        }
        .cls-1,
        .cls-2,
        .cls-3 {
          stroke-miterlimit: 10;
        }
        .cls-2 {
          fill: #ffcd02;
          stroke: #000;
        }
        .cls-3 {
          fill: rgba(0, 0, 0, 0.25);
          stroke: #020202;
        }
      `}
      </style>
    </defs>
    <rect className="cls-1" x="2.38" y="9.73" width="22.76" height="13.66" />
    <polygon className="cls-2" points="13.76 .62 1.51 9.73 26.01 9.73 13.76 .62" />
    <rect className="cls-3" x="15.83" y="14.28" width="4.55" height="9.1" />
    <rect
      className="cls-3"
      x="6.5"
      y="14.05"
      width="4.55"
      height="5.01"
      transform="translate(-7.78 25.33) rotate(-90)"
    />
  </svg>
);

const PointedRoof = ({ width = 42, height = 42, className, ariaHidden }: IconProps) => (
  <svg
    width={width}
    height={height}
    className={className}
    id="Ebene_1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 26.62 30.7"
    aria-hidden={ariaHidden}
  >
    <defs>
      <style>
        {`
      .cls-1 {
        fill: #fff;
        stroke: #1d1d1b;
      }
      .cls-1,
      .cls-2,
      .cls-3 {
        stroke-miterlimit: 10;
      }
      .cls-2 {
        fill: #ffcd02;
        stroke: #000;
      }
      .cls-3 {
        fill: rgba(0, 0, 0, 0.25);
        stroke: #020202;
      }
      `}
      </style>
    </defs>
    <rect className="cls-1" x="1.9" y="16.5" width="22.82" height="13.69" />
    <polygon className="cls-2" points="13.31 .81 1.03 16.5 25.59 16.5 13.31 .81" />
    <rect className="cls-3" x="15.38" y="21.07" width="4.56" height="9.13" />
    <rect
      className="cls-3"
      x="6.03"
      y="20.84"
      width="4.56"
      height="5.02"
      transform="translate(-15.04 31.66) rotate(-90)"
    />
  </svg>
);

export const Icon = {
  ArrowLeft,
  ArrowRight,
  Checkmark,
  Check,
  Close,
  DoubleHouse,
  Exclamationmark,
  House,
  Info,
  TrippleHouse,
  RoofFlat,
  Roof15Deg,
  Roof30Deg,
  Roof45Deg,
  Savings,
  Verify,
  RoofNorth,
  RoofSouth,
  RoofWest,
  RoofEast,
  FlatRoof,
  SlopedRoof,
  NormalRoof,
  PointedRoof
};

export type IconType = keyof typeof Icon;
export const isIconType = (icon?: string): icon is IconType => icon !== undefined && icon in Icon;

export const getIcon = (icon: IconType) => Icon[icon];
